import React, { useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons'
import type { Competency } from '../../types/GrowthWheelManagement'
import {
  useDeleteEditorCompetencyMutation,
  useReorderEditorSubCompetenciesMutation,
} from '../../redux/growth-wheel.endpoints'
import { getLevelCompletionStatus } from './wheelManagementUtils'
import { RichTextView } from './RichTextView'
import { WheelManagementDragHandle } from './WheelManagementDragHandle'
import { WheelManagementDeleteModal } from './WheelManagementDeleteModal'
import {
  getGwWmActiveDrag,
  getGwWmDragData,
  setGwWmActiveDrag,
  setGwWmDragData,
  setGwWmDragPreviewFromNode,
} from './wheelManagementDnD'

type Props = {
  growthWheelId: number
  competency: Competency
  areaId: string
  /** Parent competency (area) index in the framework list — for area drag-and-drop. */
  areaIndex: number
  compIndex: number
  /** Ordered sub-competencies under this area (same `area` id). */
  siblingCompetencies: Competency[]
  onEdit: () => void
  onAreaReorderDrag: (fromIndex: number, toIndex: number) => void
  subDropTargetIndex: number | null
  onSubDropTargetChange: (index: number | null) => void
}

export function SubCompetencyRow({
  growthWheelId,
  competency,
  areaId,
  areaIndex,
  compIndex,
  siblingCompetencies,
  onEdit,
  onAreaReorderDrag,
  subDropTargetIndex,
  onSubDropTargetChange,
}: Props) {
  const rowRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [deleteCompetency] = useDeleteEditorCompetencyMutation()
  const [reorderSubCompetencies] = useReorderEditorSubCompetenciesMutation()

  const levelStatus = getLevelCompletionStatus(competency)
  const isComplete = levelStatus.defined === levelStatus.total
  const isDropTarget = subDropTargetIndex === compIndex && !isDragging

  const persistSubOrder = async (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= siblingCompetencies.length ||
      toIndex >= siblingCompetencies.length
    ) {
      return
    }
    const next = [...siblingCompetencies]
    const [item] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, item)
    await reorderSubCompetencies({
      growthWheelId,
      parentCompetencyId: Number(areaId),
      orderedSubCompetencyIds: next.map(c => Number(c.id)),
    }).unwrap()
  }

  const confirmDelete = async () => {
    await deleteCompetency({
      growthWheelId,
      competencyId: Number(competency.id),
    }).unwrap()
    setDeleteModalOpen(false)
  }

  const handleCompetencyDragStart = (e: React.DragEvent<HTMLSpanElement>) => {
    setGwWmDragData(e.dataTransfer, {
      kind: 'competency',
      areaId,
      fromIndex: compIndex,
    })
    setGwWmDragPreviewFromNode(
      e.dataTransfer,
      e.clientX,
      e.clientY,
      rowRef.current,
    )
    setGwWmActiveDrag({
      kind: 'competency',
      areaId,
      fromIndex: compIndex,
    })
    setIsDragging(true)
  }

  const handleCompetencyDragEnd = () => {
    setGwWmActiveDrag(null)
    onSubDropTargetChange(null)
    setIsDragging(false)
  }

  const handleRowDragOver = (e: React.DragEvent) => {
    const active = getGwWmActiveDrag()
    if (!active) return

    if (active.kind === 'competency' && active.areaId === areaId) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (active.fromIndex !== compIndex) {
        onSubDropTargetChange(compIndex)
      }
      return
    }

    if (active.kind === 'area') {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }

  const handleRowDrop = (e: React.DragEvent) => {
    const p = getGwWmDragData(e)
    if (!p) return

    if (p.kind === 'competency' && p.areaId === areaId) {
      e.preventDefault()
      e.stopPropagation()
      onSubDropTargetChange(null)
      if (p.fromIndex !== compIndex) {
        void persistSubOrder(p.fromIndex, compIndex)
      }
      return
    }

    if (p.kind === 'area') {
      e.preventDefault()
      e.stopPropagation()
      if (p.fromIndex !== areaIndex) {
        onAreaReorderDrag(p.fromIndex, areaIndex)
      }
    }
  }

  return (
    <>
      <div
        ref={rowRef}
        className={`gw-wm__sub-row${isDragging ? ' gw-wm__sub-row--dragging' : ''}${isDropTarget ? ' gw-wm__sub-row--drop-target' : ''}`}
        aria-dropeffect={isDropTarget ? 'move' : undefined}
        onDragOver={handleRowDragOver}
        onDrop={handleRowDrop}
      >
        <WheelManagementDragHandle
          enabled
          onDragStart={handleCompetencyDragStart}
          onDragEnd={handleCompetencyDragEnd}
          title="Drag to reorder sub-competency"
          className="gw-wm__drag-handle--sub"
        />
        <div className="gw-wm__sub-row-main">
          <div className="gw-wm__sub-row-text">
            <div className="gw-wm__sub-row-title">
              <h4 className="gw-wm__sub-name">{competency.name}</h4>
              <span
                className={`gw-wm__badge ${
                  isComplete ? 'gw-wm__badge--ok' : 'gw-wm__badge--warn'
                }`}
              >
                {levelStatus.defined}/{levelStatus.total} rubric levels defined
              </span>
            </div>
            <div draggable={false}>
              <RichTextView
                className="gw-wm__sub-desc"
                html={competency.description}
                fallback={
                  <p className="gw-wm__sub-desc gw-wm__desc-placeholder">
                    No description yet.
                  </p>
                }
              />
            </div>
          </div>
        </div>
        <div className="gw-wm__area-actions">
          <button
            type="button"
            className="gw-wm__btn-icon"
            draggable={false}
            title="Edit sub-competency"
            onClick={onEdit}
          >
            <FontAwesomeIcon icon={faPenToSquare} size="sm" />
          </button>
          <button
            type="button"
            className="gw-wm__btn-icon gw-wm__btn-icon--danger"
            draggable={false}
            title="Delete sub-competency"
            onClick={() => setDeleteModalOpen(true)}
          >
            <FontAwesomeIcon icon={faTrash} size="sm" />
          </button>
        </div>
      </div>

      {deleteModalOpen && (
        <WheelManagementDeleteModal
          title="Delete sub-competency"
          confirmLabel="Delete sub-competency"
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={() => void confirmDelete()}
        >
          <p className="gw-wm__hint">
            Are you sure you want to delete &quot;{competency.name}&quot;? This
            will also delete all levels 1–5 rubric definitions for this
            sub-competency and remove all associated assessment scores and notes.
          </p>
        </WheelManagementDeleteModal>
      )}
    </>
  )
}
